/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { ChangePasswordSchema, type ChangePasswordInput } from '@therapy/shared-validators';
import { Check, KeyRound } from 'lucide-react';
import { useCallback, useState, type SyntheticEvent, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { apiClient } from '../../lib/api/client.js';
import { FormField } from '../form/form-field.js';
import { Button } from '../ui/button.js';

interface ChangePasswordFormProps {
  readonly onDone: () => void;
}

/** Change the signed-in user's password — verifies the current one server-side. */
export function ChangePasswordForm({ onDone }: ChangePasswordFormProps): JSX.Element {
  const { t } = useTranslation();
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordInput>({ resolver: zodResolver(ChangePasswordSchema) });

  const submit = useCallback(
    async (values: ChangePasswordInput): Promise<void> => {
      setServerError(null);
      try {
        await apiClient.post('/auth/change-password', values);
        setDone(true);
      } catch {
        setServerError(t('account.changePassword.error'));
      }
    },
    [t],
  );

  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  if (done) {
    return (
      <div className="space-y-4">
        <p className="flex items-center gap-2 text-sm text-emerald-600">
          <Check className="h-4 w-4" aria-hidden="true" />
          {t('account.changePassword.success')}
        </p>
        <div className="flex justify-end">
          <Button type="button" onClick={onDone}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
      <FormField
        id="cp-current"
        type="password"
        label={t('account.changePassword.current')}
        autoComplete="current-password"
        error={errors.currentPassword?.message}
        registration={register('currentPassword')}
      />
      <FormField
        id="cp-new"
        type="password"
        label={t('account.changePassword.new')}
        autoComplete="new-password"
        error={errors.newPassword?.message}
        registration={register('newPassword')}
      />
      {serverError !== null && (
        <p role="alert" className="text-sm text-red-600">
          {serverError}
        </p>
      )}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          <KeyRound className="h-4 w-4" aria-hidden="true" />
          {t('account.changePassword.submit')}
        </Button>
      </div>
    </form>
  );
}
